/**
 * Product Media Management System - Moon Moon Agency
 * Handles media display, variant-specific images, and slider interactions
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION
 */
const MEDIA_CONFIG = {
	imageWidths: {
		main: 1000,
		thumbnail: 500
	},
	
	// Selectors
	selectors: {
		productMedias: '[data-product-medias] [data-splide-slide]',
		variantMediaData: '#variant-media-data',
		mainSlider: '[data-main-slider]:not([data-variant-slider])',
		mainSliderVariant: '[data-main-slider][data-variant-slider]',
		thumbnailSliderVariant: '[data-thumbnail-slider][data-variant-slider]',
		splideSlide: '[data-splide-slide]',
		splideList: '[data-splide-list]',
		slideImage: 'img',
		dataId: '[data-id="{{id}}"]'
	},
	
	// CSS Classes
	classes: {
		mainProductMediaElem: 'mm-main-product-media-elem',
		thumbnailMedia: 'mm-thumbnail-media'
	},

	// Development mode (lazy evaluation)
	get isDevelopment() {
		return typeof isDevelopment !== 'undefined' ? isDevelopment : false;
	}
};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Media-specific logging
const mediaLog = (level, message, data = {}) => {
	if (!MEDIA_CONFIG.isDevelopment && level === 'info') return;
	if (typeof console !== 'undefined') {
		const logFn = console[level] || console.log;
		logFn(`📸 [MEDIA] ${message}`, { timestamp: window.getTimestamp?.() || new Date().toLocaleString(), ...data });
	}
};

// Media Event Emitter
const mediaEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'media'
	};
	
	return document.dispatchEvent(new CustomEvent(`product:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

/*********************************************************************************************
 * 📸 MEDIA MANAGEMENT
 */
const MediaManager = {
	updateMedia(variant) {
		if (!variant?.featured_media?.id) return;

		try {
			const variantMediaId = variant.featured_media.id;
			const allMainMedia = document.querySelectorAll(MEDIA_CONFIG.selectors.productMedias);

			if (!allMainMedia.length) {
				mediaLog('info', 'No media slides found');
				return;
			}

			// Find the media slide with matching ID
			const targetSlideIndex = Array.from(allMainMedia).findIndex(media => {
				const mediaId = media.dataset.id;
				return mediaId && parseInt(mediaId, 10) === variantMediaId;
			});

			// Navigate to the slide if found and splide instance exists
			if (targetSlideIndex !== -1) {
				if (window.splideMainInstance && typeof window.splideMainInstance.go === 'function') {
					window.splideMainInstance.go(targetSlideIndex);
				} else {
					mediaLog('info', 'Splide instance not available or invalid');
				}
			} else {
				mediaLog('info', 'Target slide not found for media ID', { variantMediaId });
			}
			
		} catch (error) {
			mediaLog('error', 'Failed to update media', { error: error.message });
		}
	},

	/**
	 * Update variant images
	 */
	updateImagesVariant(variant) {
		if (!variant?.id) return;

		const variantId = variant.id.toString();
		mediaEmit('variant-media-switch-start', { variantId });
		
		// Check if we have variant-specific media data
		const variantMediaData = this.getVariantMediaData();
		
		if (variantMediaData && variantMediaData[variantId]) {
			// Handle variant-specific images (complex case)
			this.handleVariantSpecificMedia(variantId, variantMediaData);
		} else {
			// Handle standard product - navigate to variant's featured media
			this.handleStandardProductMedia(variant);
		}
	},

	/**
	 * Get variant media data
	 */
	getVariantMediaData() {
		try {
			const dataScript = document.querySelector(MEDIA_CONFIG.selectors.variantMediaData);
			if (!dataScript) return null;
			return JSON.parse(dataScript.textContent);
		} catch (error) {
			mediaLog('error', 'Failed to parse variant media data', { error: error.message });
			return null;
		}
	},

	/**
	 * Handle variant-specific media replacement
	 */
	handleVariantSpecificMedia(variantId, variantMediaData) {
		try {
			const variantMedia = variantMediaData[variantId].media;
			
			if (!Array.isArray(variantMedia) || variantMedia.length === 0) {
				mediaLog('error', 'Invalid variant media data', { variantId, variantMedia });
				mediaEmit('variant-media-switch-failed', { variantId, reason: 'invalid-media-data' });
				return;
			}
			
			this.updateSliderContent(variantId, variantMedia);
			mediaEmit('variant-media-switch-success', { variantId, type: 'variant-specific' });
		} catch (error) {
			mediaLog('error', 'Failed to update variant images', { error: error.message });
			mediaEmit('variant-media-switch-error', { variantId, error: error.message });
		}
	},

	/**
	 * Handle standard product - navigate to variant's featured media
	 */
	handleStandardProductMedia(variant) {
		try {
			const variantId = variant.id.toString();
			
			if (!variant.featured_media?.id) {
				mediaEmit('variant-media-switch-success', { variantId, type: 'no-featured-media' });
				return;
			}
			
			// Use the simple updateMedia method for standard navigation
			this.updateMedia(variant);
			
			// Also try the more advanced slider navigation if ProductMediaSliders exists
			const featuredMediaId = variant.featured_media.id;
			const mainSlider = document.querySelector(MEDIA_CONFIG.selectors.mainSlider);
			
			if (mainSlider) {
				// Find slide with matching media ID
				const targetSlide = mainSlider.querySelector(MEDIA_CONFIG.selectors.dataId.replace('{{id}}', featuredMediaId));
				if (targetSlide) {
					// Get slide index
					const slideIndex = parseInt(targetSlide.dataset.index, 10);
					if (!isNaN(slideIndex)) {
						// Navigate using ProductMediaSliders if available
						const sliderPair = window.ProductMediaSliders?.instances?.['main'];
						if (sliderPair?.main?.go) {
							sliderPair.main.go(slideIndex);
							mediaEmit('variant-media-switch-success', { variantId, type: 'featured-media-navigation', slideIndex });
							return;
						}
					}
				}
			}
			
			mediaEmit('variant-media-switch-success', { variantId, type: 'standard-navigation' });
		} catch (error) {
			mediaLog('error', 'Failed to navigate to featured media', { error: error.message });
			mediaEmit('variant-media-switch-error', { variantId: variant.id.toString(), error: error.message });
		}
	},

	/**
	 * Update slider content with new variant media
	 */
	updateSliderContent(variantId, variantMedia) {
		const mainSlider = document.querySelector(MEDIA_CONFIG.selectors.mainSliderVariant);
		const thumbSlider = document.querySelector(MEDIA_CONFIG.selectors.thumbnailSliderVariant);
		
		if (!mainSlider || !thumbSlider) {
			mediaLog('error', 'Variant sliders not found');
			return;
		}
		
		// Update data-variantId attributes
		mainSlider.setAttribute('data-variantId', variantId);
		thumbSlider.setAttribute('data-variantId', variantId);
		
		// Update existing images instead of regenerating HTML
		this.updateExistingImages(mainSlider, thumbSlider, variantMedia);
		
		// Refresh Splide to handle potential slide count changes
		this.refreshSliders(variantMedia.length);
	},

	/**
	 * Update existing images or add/remove slides as needed
	 */
	updateExistingImages(mainSlider, thumbSlider, variantMedia) {
		const slides = {
			main: mainSlider.querySelectorAll(MEDIA_CONFIG.selectors.splideSlide),
			thumb: thumbSlider.querySelectorAll(MEDIA_CONFIG.selectors.splideSlide)
		};
		
		const configs = [
			{ slides: slides.main, slider: mainSlider, width: MEDIA_CONFIG.imageWidths.main, className: MEDIA_CONFIG.classes.mainProductMediaElem },
			{ slides: slides.thumb, slider: thumbSlider, width: MEDIA_CONFIG.imageWidths.thumbnail, className: MEDIA_CONFIG.classes.thumbnailMedia }
		];
		
		// Update or add slides for each slider
		configs.forEach(({ slides, slider, width, className }) => {
			variantMedia.forEach((media, index) => {
				if (slides[index]) {
					this.updateSlideImage(slides[index], media, width, className, index);
				} else {
					this.addNewSlide(slider, media, width, className, index);
				}
			});
			
			// Remove extra slides if variant has fewer images
			this.removeExtraSlides(slider, variantMedia.length);
		});
	},

	/**
	 * Update an existing slide's image
	 */
	updateSlideImage(slide, media, width, className, index) {
		const img = slide.querySelector(MEDIA_CONFIG.selectors.slideImage);
		if (!img || !media) {
			mediaLog('error', 'Invalid media or image element', { hasImg: !!img, media });
			return;
		}
		
		try {
			// Extract media properties based on type
			const mediaData = this.extractMediaData(media);
			if (!mediaData.url) return;
			
			// Update image attributes
			const imageUrl = this.getShopifyImageUrl(mediaData.url, width);
			img.src = imageUrl;
			img.alt = mediaData.alt;
			img.className = className;
			
			// Update responsive srcset
			if (img.srcset) {
				img.srcset = this.generateResponsiveSrcset(mediaData.url);
			}
			
			// Update responsive dimensions if available
			if (mediaData.width && mediaData.height) {
				img.setAttribute('width', mediaData.width);
				img.setAttribute('height', mediaData.height);
			}
			
			// Update slide navigation attributes
			slide.setAttribute('data-id', mediaData.id);
			slide.setAttribute('data-index', index);
		} catch (error) {
			mediaLog('error', 'Failed to update slide image', { media, width, className, index, error: error.message });
		}
	},

	/**
	 * Add a new slide (clone existing structure)
	 */
	addNewSlide(slider, media, width, className, index) {
		const existingSlide = slider.querySelector(MEDIA_CONFIG.selectors.splideSlide);
		if (existingSlide) {
			const newSlide = existingSlide.cloneNode(true);
			
			// Clear existing data before updating
			newSlide.removeAttribute('data-id');
			newSlide.removeAttribute('data-index');
			
			// Update the slide with new media
			this.updateSlideImage(newSlide, media, width, className, index);
			
			// Append to slider
			slider.querySelector(MEDIA_CONFIG.selectors.splideList).appendChild(newSlide);
		}
	},

	/**
	 * Remove extra slides when variant has fewer images
	 */
	removeExtraSlides(slider, keepCount) {
		const slides = slider.querySelectorAll(MEDIA_CONFIG.selectors.splideSlide);
		for (let i = slides.length - 1; i >= keepCount; i--) {
			slides[i].remove();
		}
	},

	/**
	 * Refresh Splide sliders after content changes
	 */
	refreshSliders(newSlideCount) {
		const sliderId = 'variant';
		const sliderPair = window.ProductMediaSliders?.instances?.[sliderId];
		
		if (sliderPair?.main?.refresh) {
			try {
				// Refresh instead of destroying/recreating
				sliderPair.main?.refresh();
				sliderPair.thumbnail?.refresh();
				mediaEmit('variant-media-splide-refresh', { sliderId, slideCount: newSlideCount });
			} catch (error) {
				// Fallback: destroy and recreate if refresh fails
				mediaLog('warn', 'Splide refresh failed, recreating', { error: error.message });
				this.reloadSliders();
			}
		}
	},

	/**
	 * Fallback: Reload Splide sliders completely
	 */
	reloadSliders() {
		const sliderId = 'variant';
		mediaEmit('variant-media-splide-reload', { sliderId });
		
		if (window.ProductMediaSliders?.destroy && window.ProductMediaSliders?.init) {
			window.ProductMediaSliders.destroy(sliderId);
			window.ProductMediaSliders.init(sliderId, true);
		} else {
			mediaLog('warn', 'ProductMediaSliders methods not available for reload', {
				sliderExists: !!window.ProductMediaSliders,
				hasDestroy: !!window.ProductMediaSliders?.destroy,
				hasInit: !!window.ProductMediaSliders?.init
			});
		}
	},

	/**
	 * Extract media data from different media formats
	 */
	extractMediaData(media) {
		if (typeof media === 'string') {
			return {
				url: media,
				alt: '',
				id: this.generateMediaId(media),
				width: null,
				height: null
			};
		}
		
		if (media.src) {
			return {
				url: media.src,
				alt: media.alt || '',
				id: media.id || this.generateMediaId(media.src),
				width: media.width,
				height: media.height
			};
		}
		
		if (media.preview_image) {
			return {
				url: media.preview_image.src,
				alt: media.alt || '',
				id: media.id || this.generateMediaId(media.preview_image.src),
				width: media.preview_image.width,
				height: media.preview_image.height
			};
		}
		
		mediaLog('error', 'Invalid media format', { media });
		return { url: null };
	},

	/**
	 * Generate a consistent media ID from URL string
	 */
	generateMediaId(url) {
		// Simple hash function to generate consistent ID from URL
		let hash = 0;
		for (let i = 0; i < url.length; i++) {
			const char = url.charCodeAt(i);
			hash = ((hash << 5) - hash) + char;
			hash = hash & hash; // Convert to 32-bit integer
		}
		return Math.abs(hash).toString();
	},

	/**
	 * Generate responsive srcset for image_tag compatibility
	 */
	generateResponsiveSrcset(originalUrl) {
		if (!originalUrl || typeof originalUrl !== 'string') {
			return '';
		}
		
		// Common responsive breakpoints used by Shopify image_tag
		const breakpoints = [200, 300, 400, 500, 600, 700, 800, 900, 1000, 1200, 1400, 1600, 1800, 2000];
		
		return breakpoints
			.map(width => `${this.getShopifyImageUrl(originalUrl, width)} ${width}w`)
			.join(', ');
	},

	/**
	 * Generate Shopify image URL with specified width
	 */
	getShopifyImageUrl(originalUrl, width) {
		if (!originalUrl || typeof originalUrl !== 'string') {
			return originalUrl;
		}
		
		try {
			// Remove any existing size parameters
			let cleanUrl = originalUrl.replace(/_\d+x\d*\./g, '.');
			
			// Handle URLs with query parameters (like v= version)
			const [baseUrl, queryString] = cleanUrl.split('?');
			
			// Add width parameter before file extension
			const sizedUrl = baseUrl.replace(/(\.[^.]*$)/, `_${width}x$1`);
			
			// Reattach query string if it exists
			return queryString ? `${sizedUrl}?${queryString}` : sizedUrl;
		} catch (error) {
			mediaLog('warn', 'Failed to resize image URL, using original', { originalUrl, error: error.message });
			return originalUrl;
		}
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
// Make MediaManager globally accessible for communication with other modules
window.MediaManager = MediaManager;

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (MEDIA_CONFIG.isDevelopment) {
	// Media-specific events
	['variant-media-switch-start', 'variant-media-switch-success', 'variant-media-switch-failed', 
		'variant-media-switch-error', 'variant-media-splide-refresh', 'variant-media-splide-reload'].forEach(event => {
		document.addEventListener(`product:${event}`, e => 
			console.log(`📸 [MEDIA] ${event}:`, e.detail)
		);
	});
}